import "./TestEntry.scss";

import { Box, Card, Flex, Grid, Progress, SimpleGrid, Stack, Text, rem } from "@mantine/core";
import { EyeDisplacement, VFABehaviour } from "types/testTypes";
import { calculateAgeForTest, parseDateOnly } from "@utils/CommonUtil";
import { useEffect, useMemo, useState } from "react";

import GazeTrackMap from "./GazeTrackMap";
import BebieCurve from "./charts/BebieCurve";
import GrayScaleMap from "./gray_scale_map/GrayScaleMap";
import GrayScaleLegend from "./gray_scale_map/GrayScaleLegend";
import StatisticsLegand from "./comon/StatisticsLegand";
import { TestResultSheet } from "./SimpleTestEntry";
import { usePrintableContext, usePrintableSectionContext } from "@components";
import ThresholdMap, { NEW_DB_TILE_BASE_PATH } from "./maps/ThresholdMap";
import { VFABebiePoint } from "types/tests/specifics";
import { getDisplayedFovealThreshold, getFnFpError } from "@utils";
import { useVfaAdvancedMetricsVisible } from "@hooks/useVfaAdvancedMetricsVisible";
import { resolveVfaMapValues } from "./statistics_map/ThresholdTestUtil";
import { getTestConfig } from "./util/TestEntryUtil";
import { useTranslation } from "react-i18next";
import {
  computeZoneStats,
  getZoneSymbolKind,
  isZoneTestBehaviour,
  supportsBebieCurve,
} from "@utils/TestUtils";
import ZoneSymbolLegend from "./maps/ZoneSymbolLegend";

/*
 * t("TestEntry.flWarning")
 * t("TestEntry.fpWarning")
 * t("TestEntry.fnWarning")
 */

const GRAY_SCALE_LEGEND_WIDTH = rem(200);

const PAIRED_CHART_HEIGHT = 170;
const PAIRED_CHART_HEIGHT_EMBEDDED = 150;

export default function MonocularTestEntry(props) {
  const [eye, setEye] = useState(null);
  const [type, setType] = useState(null);
  const [meanPsd, setMeanPsd] = useState(0);
  const [meanDeviation, setMeanDeviation] = useState(0);
  const [ntd, setNtd] = useState([]);
  const [npd, setNpd] = useState([]);
  const [ntdGs, setNtdGs] = useState([]);
  const [npdGs, setNpdGs] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [gazeTrackingData, setGazeTrackingData] = useState(null);
  const [thresholds, setThresholds] = useState([]);
  const [metricsUnavailable, setMetricsUnavailable] = useState(false);
  const [vfi, setVfi] = useState(0);
  const [ght, setGht] = useState("");
  const [meanSensitivity, setMeanSensitivity] = useState(0);
  const [reliabilityFactor, setReliabilityFactor] = useState<number | null>(null);
  const [bebie, setBebie] = useState<VFABebiePoint[]>([]);
  const [fpVal] = useState(getFnFpError(props.entry, "fp", props.presetValues));
  const [warnings, setWarnings] = useState([]);
  const [bigStimuliSize] = useState(false);
  const [displacements, setDisplacements] = useState<EyeDisplacement[]>(null);

  const result = props.entry as any;
  const { t } = useTranslation();
  const isZoneTest = isZoneTestBehaviour(props.entry.testBehaviour);
  const zoneSymbolThresholds = useMemo(
    () =>
      isZoneTest
        ? thresholds.map((value) => getZoneSymbolKind(value, props.entry.testBehaviour) ?? "none")
        : [],
    [isZoneTest, thresholds, props.entry.testBehaviour],
  );
  const zoneStats = useMemo(() => {
    if (!isZoneTest) return null;
    const { emptyCellsIndex } = getTestConfig(props.entry.testType, props.entry.eye);
    return computeZoneStats(zoneSymbolThresholds, emptyCellsIndex);
  }, [isZoneTest, zoneSymbolThresholds, props.entry.testType, props.entry.eye]);
  const fovealThreshold = getDisplayedFovealThreshold(props.entry?.fovea);
  const hasAdvancedMetrics = useVfaAdvancedMetricsVisible(props.entry);
  const showBebieCurve = hasAdvancedMetrics && supportsBebieCurve(props.entry.testBehaviour);
  const { prettier } = usePrintableContext();
  const { floatingLogo } = usePrintableSectionContext();
  const isEmbeddedWithOverview = floatingLogo && prettier;
  const pairedChartHeight = isEmbeddedWithOverview
    ? PAIRED_CHART_HEIGHT_EMBEDDED
    : PAIRED_CHART_HEIGHT;

  useEffect(() => {
    if (props.updateAge) {
      const pAge = calculateAgeForTest(
        new Date(props.entry.startDate),
        parseDateOnly(props.entry.patient.dob),
      );
      props.updateAge(pAge);
    }
    setIsInitialized(true);
    if (props.entry.gaze && props.entry.gaze.length > 0) {
      addTimeValue(props.entry.startDate, props.entry.finishDate, props.entry.gaze);
    }
  }, []);

  useEffect(() => {
    if (result?.displacementList) {
      setDisplacements(result.displacementList);
    }
  }, [result]);

  function updateMdVal(md) {
    if (props.presetValues && props.presetValues.hasOwnProperty("md")) {
      setMeanDeviation(props.presetValues.md);
    } else {
      if (props.updateMd) {
        props.updateMd(md);
      }
      setMeanDeviation(md);
    }
  }

  function updatePsdVal(psd) {
    if (props.presetValues && props.presetValues.hasOwnProperty("psd")) {
      setMeanPsd(props.presetValues.psd);
    } else {
      if (props.updatePsd) {
        props.updatePsd(psd);
      }
      setMeanPsd(psd);
    }
  }

  function updateVfiVal(vfiVal) {
    let vfiCalculated = vfiVal;
    if (props.presetValues && props.presetValues.hasOwnProperty("vfi")) {
      vfiCalculated = props.presetValues.vfi;
      setVfi(vfiCalculated);
    } else {
      if (props.updateVfi) {
        vfiCalculated = vfiVal;
        props.updateVfi(vfiCalculated);
      }
      setVfi(vfiCalculated);
    }
    return Number(vfiCalculated);
  }

  function addTimeValue(initDate, endDate, valueArray) {
    const timeSeriesData = [];
    const startTime = new Date(initDate).getTime();
    const endTime = new Date(endDate).getTime();
    const totalDuration = endTime - startTime;

    // Calculate time interval between each data point
    const timeInterval = valueArray.length > 1 ? totalDuration / (valueArray.length - 1) : 0;

    valueArray.forEach((value, index) => {
      const timestamp = startTime + index * timeInterval;
      timeSeriesData.push([timestamp, value]);
    });

    setGazeTrackingData(timeSeriesData);
  }

  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    if (props.entry) {
      setType(props.entry.testType);
      setEye(props.entry.eye);
      const fl = Number(props.entry["blindSpotSeen"]) / Number(props.entry["blindSpotTotal"]);
      const ws = [];
      if (fl > 0.35) ws.push("TestEntry.flWarning");
      const fn = getFnFpError(props.entry, "fn", props.presetValues);
      const fp = fpVal;
      if (fp > 35) ws.push("TestEntry.fpWarning");
      if (fn > 35) ws.push("TestEntry.fnWarning");
      setWarnings(ws);
    }

    const values = resolveVfaMapValues(props.entry);
    setMetricsUnavailable(!values);
    if (!values) return;

    setThresholds(values.thresholds);
    setNtd(values.ntd);
    setNpd(values.npd);
    setNtdGs(values.ntdGs);
    setNpdGs(values.npdGs);
    updateMdVal(values.md);
    updatePsdVal(values.psd);
    updateVfiVal(values.vfi);
    setGht(values.ght);
    setMeanSensitivity(values.meanSensitivity);
    setReliabilityFactor(values.reliabilityFactor);
    setBebie(values.bebie);
  }, [isInitialized]);

  function getTestContent() {
    if (metricsUnavailable) {
      return (
        <Text c="red.6" role="alert">
          {t("OpaReports.metricsUnavailableWarningText", { count: 1 })}
        </Text>
      );
    }
    // no animation gate here, a cosmetic progress bar used to end up in
    // printed PDFs (thresholds derive synchronously from props anyway).
    const hasBebieCurve = showBebieCurve && bebie.length > 0;
    const hasSecondChart = Boolean(gazeTrackingData) || displacements?.length > 0;
    return thresholds.length > 0 ? (
      <Grid w="100%" display="flex">
        <Grid.Col span={12} w="100%">
          <Grid columns={24}>
            <Grid.Col span={9} py="xs" h="100%" w="100%">
              <ThresholdMap
                thresholds={isZoneTest ? zoneSymbolThresholds : thresholds}
                testType={type}
                eye={eye}
                showAngleOnAxis={true}
                content={isZoneTest ? "zoneSymbol" : "value"}
                fovealThreshold={isZoneTest ? undefined : fovealThreshold}
                bsAngles={props.entry.bsAngles}
              />
            </Grid.Col>
            {isZoneTest ? (
              <Grid.Col offset={1} span={9} py="md">
                <Stack h="100%" justify="space-between" align="flex-start">
                  <SimpleGrid cols={2} spacing="xl" verticalSpacing={0}>
                    <Text fz="md" fw={600} ta="right">
                      {t("pointsSeen")}
                    </Text>
                    <Text fz="md" fw={600} ta="right">
                      {zoneStats.pointsSeen}
                    </Text>
                    <Text fz="md" fw={600} ta="right">
                      {t("pointsMissed")}
                    </Text>
                    <Text fz="md" fw={600} ta="right">
                      {zoneStats.pointsMissed}
                    </Text>
                    <Text fz="md" fw={600} ta="right">
                      {t("efficiencyScore")}
                    </Text>
                    <Text fz="md" fw={600} ta="right">
                      {zoneStats.efficiencyScore}%
                    </Text>
                  </SimpleGrid>
                  <ZoneSymbolLegend testBehaviour={props.entry.testBehaviour} />
                </Stack>
              </Grid.Col>
            ) : (
              <Grid.Col offset={1} span={9} py="xs" h="100%" w="100%" pos="relative">
                <GrayScaleMap input={thresholds} testType={type} eye={eye} showAxisGutters />
                {hasAdvancedMetrics && (
                  <Box pos="absolute" left="100%" bottom={0} w={GRAY_SCALE_LEGEND_WIDTH}>
                    <GrayScaleLegend />
                  </Box>
                )}
              </Grid.Col>
            )}
          </Grid>
          {bigStimuliSize || isZoneTest ? null : (
            <>
              <Grid columns={24} gutter="xs">
                <Grid.Col span={7}>
                  <Stack gap={0}>
                    <ThresholdMap
                      thresholds={ntd}
                      testType={type}
                      eye={eye}
                      checkEmptyCells={true}
                    />

                    <Text fz="md" fw={600} ta="center">
                      {t("totalDeviation")}
                    </Text>
                    <ThresholdMap
                      thresholds={ntdGs}
                      testType={type}
                      eye={eye}
                      checkEmptyCells={true}
                      content="probabilityTile"
                      probabilityTileBasePath={NEW_DB_TILE_BASE_PATH}
                    />
                  </Stack>
                </Grid.Col>
                <Grid.Col span={7} offset={1}>
                  <Stack h="100%" justify="space-evenly" gap={0}>
                    {meanDeviation < -20 ? (
                      <Flex justify="center" align="center">
                        <Text fz="md" c="red.7" fw={600} ta="center">
                          {t("severely_depressed_field_warning")}
                        </Text>
                      </Flex>
                    ) : (
                      <ThresholdMap
                        thresholds={npd}
                        testType={type}
                        eye={eye}
                        checkEmptyCells={true}
                      />
                    )}
                    <Text fz="md" fw={600} ta="center">
                      {t("patternDeviation")}
                    </Text>

                    {meanDeviation < -20 ? (
                      <Text fz="md" c="red.7" fw={600} ta="center">
                        {t("severely_depressed_field_warning")}
                      </Text>
                    ) : (
                      <ThresholdMap
                        thresholds={npdGs}
                        testType={type}
                        eye={eye}
                        checkEmptyCells={true}
                        content="probabilityTile"
                        probabilityTileBasePath={NEW_DB_TILE_BASE_PATH}
                      />
                    )}
                  </Stack>
                </Grid.Col>
                <Grid.Col span={7} offset={1}>
                  <SimpleGrid h="100%">
                    <Stack justify="center">
                      {warnings.length > 0
                        ? warnings.map((w, index) => {
                            return (
                              <Text key={`${w}-${index}`} fz="md" c="red.7" fw={600}>
                                {t(w)}
                              </Text>
                            );
                          })
                        : null}

                      <SimpleGrid cols={2} spacing="xl" verticalSpacing={0} w="100%">
                        {ght ? (
                          <>
                            <Text fz="md" fw={600} ta="right">
                              GHT
                            </Text>
                            <Text fz="md" fw={600} ta="right">
                              {ght}
                            </Text>
                          </>
                        ) : null}
                        {type !== "10-2" ? (
                          <>
                            <Text fz="md" fw={600} ta="right">
                              VFI
                            </Text>
                            <Text fz="md" fw={600} ta="right">
                              {vfi.toFixed(2) + "%"}
                            </Text>
                          </>
                        ) : null}
                        <Text fz="md" fw={600} ta="right">
                          MD
                        </Text>
                        <Text fz="md" fw={600} ta="right">
                          {(meanDeviation > 0 ? "+" : "") + meanDeviation.toFixed(2) + " dB"}
                        </Text>
                        <Text fz="md" fw={600} ta="right">
                          PSD
                        </Text>
                        <Text fz="md" fw={600} ta="right">
                          {meanPsd.toFixed(2) + " dB"}
                        </Text>
                        {hasAdvancedMetrics ? (
                          <>
                            <Text fz="md" fw={600} ta="right">
                              MS
                            </Text>
                            <Text fz="md" fw={600} ta="right">
                              {meanSensitivity.toFixed(2) + " dB"}
                            </Text>
                            <Text fz="md" fw={600} ta="right">
                              RF
                            </Text>
                            <Text fz="md" fw={600} ta="right">
                              {reliabilityFactor == null ? "—" : reliabilityFactor.toFixed(2) + "%"}
                            </Text>
                          </>
                        ) : null}
                      </SimpleGrid>
                    </Stack>
                    <StatisticsLegand cols={1} my="auto" />
                  </SimpleGrid>
                </Grid.Col>
              </Grid>
            </>
          )}
          {props.entry.testBehaviour?.toString() === VFABehaviour.SWAP_TOP.toString() ? (
            <Text>{t("SWAPdBResultExplanation")}</Text>
          ) : null}
        </Grid.Col>
        <Grid.Col span={12}>
          <Grid>
            {hasBebieCurve && (
              <Grid.Col span={hasSecondChart ? 6 : 12}>
                <BebieCurve data={bebie} height={pairedChartHeight} />
              </Grid.Col>
            )}
            {hasSecondChart && (
              <Grid.Col span={hasBebieCurve ? 6 : 12}>
                <GazeTrackMap data={gazeTrackingData ?? displacements} height={pairedChartHeight} />
              </Grid.Col>
            )}
          </Grid>
        </Grid.Col>
      </Grid>
    ) : (
      <Card padding="xl" w="20%">
        <Progress value={100} radius="md" size="lg" animated />
      </Card>
    );
  }

  return (
    <TestResultSheet
      entry={props.entry}
      getResultSheetContent={() => getTestContent()}
      canExportDicom={!isZoneTest && !metricsUnavailable}
      onPatientChanged={props.onPatientChanged}
    />
  );
}
